import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import style from './MemberVolunteerPointsTable.module.css';

const MemberVolunteerPointsTable = ({ volunteerPoints, setSearchText }) => {
  const [volunteerPointsTableData, setVolunteerPointsTableData] = useState([]);

  // Updated column structure with two-line headers and Athletes column
  const columns = [
    { key: 'volunteerName', label: 'Member Name', line1: 'Member Name', line2: '' },
    { key: 'athletes', label: 'Athletes', line1: 'Athletes', line2: '' },
    { key: 'meetsVolunteered', label: 'Meets Volunteered', line1: 'Meets', line2: 'Volunteered' },
    { key: 'eventsVolunteered', label: 'Signups Completed', line1: 'Signups', line2: 'Completed' },
    { key: 'individualHours', label: 'Individual Hours', line1: 'Individual', line2: 'Hours' },
    { key: 'totalHours', label: 'Family Hours', line1: 'Family', line2: 'Hours' },
    { key: 'requiredHours', label: 'Target Hours', line1: 'Target', line2: 'Hours' },
  ];

  const [sortKeys, setSortKeys] = useState(
    columns.reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {})
  );

  useEffect(() => {
    // Flatten the data structure to include secondary contacts as separate rows
    const flattenedData = [];
    
    volunteerPoints.forEach((volunteer) => {
      // Add primary contact row
      flattenedData.push({
        ...volunteer,
        isPrimary: true,
        isSecondary: false,
      });
      
      // Add secondary contact rows
      if (volunteer.secondaryContacts && volunteer.secondaryContacts.length > 0) {
        volunteer.secondaryContacts.forEach((secondary) => {
          flattenedData.push({
            ...secondary,
            isPrimary: false,
            isSecondary: true,
            // Secondary contacts don't have these fields
            athletes: '',
            calculationBreakdown: '',
            totalHours: null,
            requiredHours: null,
            individualPoints: null,
            totalPoints: null,
          });
        });
      }
    });
    
    setVolunteerPointsTableData(flattenedData);
  }, [volunteerPoints]);

  const handleVolunteerSearch = (e) => {
    setSearchText(e.target.value);
  };

  const sortTable = (key) => {
    // Don't sort if clicking on athletes column
    if (key === 'athletes') return;
    
    // Sort the original volunteerPoints (which maintains the structure)
    const sortedVolunteers = [...volunteerPoints].sort((a, b) => {
      if (['volunteerName', 'athletes'].includes(key)) {
        // String comparison
        const aVal = a[key] || '';
        const bVal = b[key] || '';
        return sortKeys[key]
          ? bVal.toLowerCase().localeCompare(aVal.toLowerCase())
          : aVal.toLowerCase().localeCompare(bVal.toLowerCase());
      } else {
        // Numeric comparison
        const aVal = a[key] ?? -Infinity;
        const bVal = b[key] ?? -Infinity;
        return sortKeys[key] ? bVal - aVal : aVal - bVal;
      }
    });
    
    // Rebuild flattened data with sorted volunteers
    const flattenedData = [];
    sortedVolunteers.forEach((volunteer) => {
      // Add primary contact row
      flattenedData.push({
        ...volunteer,
        isPrimary: true,
        isSecondary: false,
      });
      
      // Add secondary contact rows
      if (volunteer.secondaryContacts && volunteer.secondaryContacts.length > 0) {
        volunteer.secondaryContacts.forEach((secondary) => {
          flattenedData.push({
            ...secondary,
            isPrimary: false,
            isSecondary: true,
            athletes: '',
            calculationBreakdown: '',
            totalHours: null,
            requiredHours: null,
            individualPoints: null,
            totalPoints: null,
          });
        });
      }
    });
    
    setVolunteerPointsTableData(flattenedData);
    
    // Toggle sort direction
    setSortKeys({
      ...sortKeys,
      [key]: !sortKeys[key],
    });
  };

  return (
    <div className={style.tableCard}>
      {/* Search Bar */}
      <div className={style.searchBar}>
        <input
          type="text"
          className={style.searchInput}
          placeholder="Search members by name..."
          id="volunteerSearch"
          onInput={handleVolunteerSearch}
          autoComplete="off"
        />
      </div>

      {/* Table */}
      <div className={style.tableWrapper}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th 
                  key={index} 
                  onClick={() => col.key !== 'athletes' && sortTable(col.key)}
                  className={col.key === 'athletes' ? style.noSort : ''}
                >
                  <div className={style.headerContent}>
                    <div>{col.line1}</div>
                    {col.line2 && <div>{col.line2}</div>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {volunteerPointsTableData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                  }}
                >
                  No volunteer hours have been recorded for this season
                </td>
              </tr>
            ) : (
              volunteerPointsTableData.map((point, index) => (
                <tr key={index} className={point.isSecondary ? style.secondaryRow : ''}>
                  <td className={`${style.volunteerName} ${point.isSecondary ? style.indented : ''}`}>
                    {point.volunteerName}
                  </td>
                  <td className={style.athletesCell}>
                    {point.isPrimary && point.athletes && (
                      <div className={style.athletesWrapper}>
                        <span className={style.athletesText}>
                          {point.athletes}
                        </span>
                        {point.calculationBreakdown && (
                          <div className={style.tooltip}>
                            {point.calculationBreakdown}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{point.meetsVolunteered ?? ''}</td>
                  <td>{point.eventsVolunteered ?? ''}</td>
                  <td>
                    {point.individualHours != null ? point.individualHours.toFixed(2) : ''}
                  </td>
                  <td className={style.hoursCell}>
                    {point.isPrimary && point.totalHours != null && (
                      <>
                        {point.totalHours.toFixed(2)}
                        {point.totalHours >= point.requiredHours && <span className={style.starIcon}>⭐</span>}
                      </>
                    )}
                  </td>
                  <td className={style.hoursCell}>
                    {point.isPrimary && point.requiredHours != null ? point.requiredHours.toFixed(2) : ''}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

MemberVolunteerPointsTable.propTypes = {
  volunteerPoints: PropTypes.array,
  setSearchText: PropTypes.func,
};

export default MemberVolunteerPointsTable;